import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { useNavigationSettings } from "../NavigationSettingsContext";
import { NAVIGATION_ITEMS, ROLE_NAVIGATION_MENUS, TOP_NAVIGATION_ITEMS } from "../navigationConfig";

function Header() {
    const { token, roles, hasRole, logout } = useContext(AuthContext);
    const { navigationItems, shortcuts } = useNavigationSettings();
    const roleList = Array.isArray(roles) ? roles : roles ? [roles] : [];
    const hasRoleOr = (value) => (typeof hasRole === "function" && hasRole(value)) || roleList.includes(value);
    const isAdmin = hasRoleOr("ADMIN");
    const isDisponent = hasRoleOr("DISPONENT");
    const isMechanic = hasRoleOr("MECHANIKER");
    const isLagerist = hasRoleOr("LAGERIST");
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [openMenu, setOpenMenu] = useState(null);

    const toggleMenu = (menuKey) => {
        setOpenMenu((current) => (current === menuKey ? null : menuKey));
    };

    const closeMenus = () => setOpenMenu(null);

    const handleLinkClick = () => {
        closeMenus();
        setMenuOpen(false);
    };

    const isItemVisible = (id) => navigationItems?.[id] !== false;

    const topLevelItems = TOP_NAVIGATION_ITEMS
        .map((id) => NAVIGATION_ITEMS[id])
        .filter(Boolean)
        .filter((item) => isItemVisible(item.id))
        .filter((item) => {
            if (Array.isArray(item.requiresRoles) && item.requiresRoles.length > 0) {
                return item.requiresRoles.some((role) => hasRoleOr(role));
            }
            return true;
        });

    const roleMenus = [];
    if (isAdmin) {
        const menu = ROLE_NAVIGATION_MENUS.ADMIN;
        const items = menu.items
            .map((id) => NAVIGATION_ITEMS[id])
            .filter(Boolean)
            .filter((item) => isItemVisible(item.id));
        if (items.length > 0) {
            roleMenus.push({ key: "ADMIN", label: menu.label, items });
        }
    }
    if (isDisponent) {
        const menu = ROLE_NAVIGATION_MENUS.DISPONENT;
        const items = menu.items
            .map((id) => NAVIGATION_ITEMS[id])
            .filter(Boolean)
            .filter((item) => isItemVisible(item.id));
        if (items.length > 0) {
            roleMenus.push({ key: "DISPONENT", label: menu.label, items });
        }
    }
    if (isLagerist) {
        const menu = ROLE_NAVIGATION_MENUS.LAGERIST;
        const items = menu.items
            .map((id) => NAVIGATION_ITEMS[id])
            .filter(Boolean)
            .filter((item) => isItemVisible(item.id));
        if (items.length > 0) {
            roleMenus.push({ key: "LAGERIST", label: menu.label, items });
        }
    }
    if (isMechanic) {
        const menu = ROLE_NAVIGATION_MENUS.MECHANIKER;
        const items = menu.items
            .map((id) => NAVIGATION_ITEMS[id])
            .filter(Boolean)
            .filter((item) => isItemVisible(item.id));
        if (items.length > 0) {
            roleMenus.push({ key: "MECHANIKER", label: menu.label, items });
        }
    }
    return (
        <header className="bg-blue-600 text-white p-4 relative z-50">
            <div className="flex justify-between items-center">
                <h1 className="text-xl font-bold">
                    <Link to="/" className="hover:underline">Autoservice Groß GmbH - KFZ-Service</Link>
                </h1>
                <button
                    className="md:hidden"
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label="Menü"
                >
                    ☰
                </button>
            </div>

            {/* Navigation */}
            <nav
                className={`${menuOpen ? "flex" : "hidden"} md:flex flex-col md:flex-row gap-6 items-center mt-4 md:mt-0 w-full`}
            >
                {topLevelItems.map((item) => (
                    <Link
                        key={item.id}
                        to={item.path}
                        className="hover:underline"
                        onClick={handleLinkClick}
                    >
                        {item.label}
                    </Link>
                ))}
                {token && roleMenus.map(({ key, label, items }) => (
                    <div className="relative" key={key}>
                        <button
                            onClick={() => toggleMenu(key)}
                            className="hover:underline flex items-center"
                        >
                            {label}
                        </button>
                        <div
                            className={`${openMenu === key ? "flex" : "hidden"} flex-col md:absolute md:bg-blue-600 md:p-4 md:rounded md:mt-2 md:z-50`}
                        >
                            {items.map((item, index) => (
                                <Link
                                    key={`${item.path}-${index}`}
                                    to={item.path}
                                    className="hover:underline"
                                    onClick={handleLinkClick}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </div>
                    </div>
                ))}
                {token ? (
                    <button
                        onClick={() => {
                            logout();
                            navigate("/login");
                        }}
                        className="hover:underline md:ml-auto"
                    >
                        Logout
                    </button>
                ) : (
                    <Link
                        to="/login"
                        className="hover:underline md:ml-auto"
                        aria-label="Login"
                        title="Login"
                    >
                        🔒
                    </Link>
                )}
            </nav>
            {token && shortcuts.length > 0 && (
                <div className="mt-4 border-t border-blue-500/40 pt-3">
                    <div className="flex flex-wrap gap-3">
                        {shortcuts.map((shortcut) => {
                            const key = shortcut.id || `${shortcut.label}-${shortcut.url}`;
                            const isInternal = typeof shortcut.url === "string" && shortcut.url.startsWith("/");
                            const content = (
                                <>
                                    {shortcut.icon && (
                                        <span className="text-lg" aria-hidden="true">
                                            {shortcut.icon}
                                        </span>
                                    )}
                                    <span>{shortcut.label}</span>
                                </>
                            );
                            if (isInternal) {
                                return (
                                    <Link
                                        key={key}
                                        to={shortcut.url}
                                        className="flex items-center gap-2 rounded bg-blue-500 px-3 py-1 text-sm font-medium hover:bg-blue-400"
                                        onClick={handleLinkClick}
                                    >
                                        {content}
                                    </Link>
                                );
                            }
                            return (
                                <a
                                    key={key}
                                    href={shortcut.url}
                                    className="flex items-center gap-2 rounded bg-blue-500 px-3 py-1 text-sm font-medium hover:bg-blue-400"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {content}
                                </a>
                            );
                        })}
                    </div>
                </div>
            )}
        </header>
    );
}

export default Header;
